/**
 * Turning whatever a carrier put in `ForwardedFrom` into a stable routing key
 * (doc 18 §6.2).
 *
 * The problem this solves, measured on live PL calls: two operators send the
 * same fact in two different shapes.
 *
 *   Orange     →  sip:+48570200300@10.10.50.54:5060
 *   T-Mobile   →  sip:600200300@10.10.50.54:5060
 *
 * That is not a bug at either end — it is the ISUP Nature-of-Address indicator
 * surviving into SIP: `international` renders `+<cc><nsn>`, `national` renders
 * a bare `<nsn>`. Rather than keep a per-operator format table (which is a
 * standing invitation to be surprised by operator number five), we reduce every
 * shape to the **national significant number** and key on that. All four of
 * these produce the same key:
 *
 *   +48600200300 · 0048600200300 · 0600200300 · 600200300   →   PL-600200300
 *
 * The rules below are deterministic and total: the same input always yields the
 * same output, and anything we cannot reduce with confidence yields `null`
 * rather than a guess. Both properties are load-bearing — the claim path and
 * the call path derive the key from this one function, so a value that
 * normalises differently between them would silently misroute a business's
 * calls, and a value normalised *creatively* would route them to someone else's
 * assistant.
 */
/** E.164 country calling codes for every country we hold numbers in. Includes
 * GB/US because forward numbers bought under the old fallback cascade (doc 13a
 * D2, since retired) still exist. */
export const DIALING_CODES = {
    PL: '48',
    DE: '49',
    FR: '33',
    GB: '44',
    US: '1',
};
/**
 * Expected national-significant-number length per country. An exact number
 * where the plan is fixed-length, a `[min, max]` range where it is not.
 *
 * This is the only guard against a mis-strip: `48600200300` could in principle
 * be a country code plus a 9-digit Polish number, or an 11-digit national one.
 * Poland's plan is fixed at 9, so the ambiguity resolves. Germany's is not,
 * which is why DE gets a range and why a DE number is more likely to fall back
 * to "leave it alone" than a PL one.
 */
const NSN_LENGTH = {
    PL: 9,
    DE: [6, 11],
    FR: 9,
    GB: 10,
    US: 10,
};
/** Country codes longest-first, so `+1` never shadows a longer code. */
const CODES_BY_LENGTH = Object.entries(DIALING_CODES).sort((a, b) => b[1].length - a[1].length);
function lengthOk(iso, nsn) {
    const spec = NSN_LENGTH[iso];
    if (spec === undefined)
        return nsn.length >= 6 && nsn.length <= 15;
    if (typeof spec === 'number')
        return nsn.length === spec;
    return nsn.length >= spec[0] && nsn.length <= spec[1];
}
/**
 * Pull the user part out of a SIP/TEL URI, or pass a plain number through.
 * `sip:+48570200300@10.10.50.54:5060` → `+48570200300`.
 *
 * Everything after `@` is the carrier's own private host and means nothing to
 * us; everything after `;` is header parameters (`reason`, `privacy`, …), which
 * Telnyx normally strips before we see them but a future carrier might not.
 *
 * The URI may also arrive as a SIP *name-addr* — an optional display name then
 * the URI in angle brackets, which is what RFC 3261 actually specifies for
 * `Diversion`/`History-Info` and what a carrier that does not pre-flatten the
 * header will send us:
 *
 *   "Reception" <sip:+48510200300@host;transport=udp>;reason=unconditional
 *
 * The brackets have to come off BEFORE the scheme is matched, or the anchored
 * scheme regex never fires and the whole value falls through as garbage. Note
 * the parameter split must happen inside the brackets too: `;transport=udp`
 * above sits within the URI, not after it.
 */
export function sipUserPart(raw) {
    let v = raw.trim();
    // name-addr: take what is between the angle brackets and discard the display
    // name in front and any header parameters behind.
    const angled = /<([^>]*)>/.exec(v);
    if (angled)
        v = angled[1].trim();
    const scheme = /^(sips?|tel):/i.exec(v);
    if (scheme)
        v = v.slice(scheme[0].length);
    v = v.split('@')[0];
    v = v.split(';')[0];
    return v.trim();
}
/**
 * Reduce any representation of a phone number to `{ iso, nsn }`, or `null` when
 * it cannot be done safely.
 *
 * `defaultIso` is the country to assume when the value does not carry its own
 * country code — for an inbound call that is the country of the **pool number
 * the call arrived on**, not the business's, because the business is precisely
 * what we are trying to discover. That substitution is sound because a divert
 * to a foreign number would make every customer call an international call for
 * the owner, which doc 13a D2 already rules out.
 */
export function toNsn(raw, defaultIso) {
    if (!raw)
        return null;
    const user = sipUserPart(raw);
    if (!user || /^(anonymous|unavailable|restricted)$/i.test(user))
        return null;
    const iso = defaultIso.toUpperCase();
    // Visual separators are decoration; anything else (letters in a SIP user part
    // like `voicemail`) means this is not a phone number at all.
    const cleaned = user.replace(/[\s().-]/g, '');
    if (!/^\+?\d+$/.test(cleaned))
        return null;
    const international = cleaned.startsWith('+')
        ? cleaned.slice(1)
        : cleaned.startsWith('00')
            ? cleaned.slice(2)
            : null;
    if (international !== null) {
        // Explicitly international: the number names its own country, which may not
        // be `defaultIso` — an owner with a foreign line diverting to us is rare but
        // legitimate, and keying it under the wrong country would lose the match.
        for (const [code, prefix] of CODES_BY_LENGTH) {
            if (international.startsWith(prefix)) {
                const nsn = international.slice(prefix.length);
                return lengthOk(code, nsn) ? { iso: code, nsn } : null;
            }
        }
        return null; // a country we hold no numbers in — refuse rather than guess
    }
    // National trunk prefix (DE, FR use it; PL does not).
    if (cleaned.startsWith('0')) {
        const nsn = cleaned.replace(/^0+/, '');
        return lengthOk(iso, nsn) ? { iso, nsn } : null;
    }
    // Bare digits. Usually already the NSN — but a gateway that rendered the
    // country code without a `+` looks identical, so strip it only when doing so
    // produces a valid length AND leaving it does not.
    const cc = DIALING_CODES[iso];
    if (cc && cleaned.startsWith(cc) && !lengthOk(iso, cleaned)) {
        const stripped = cleaned.slice(cc.length);
        if (lengthOk(iso, stripped))
            return { iso, nsn: stripped };
    }
    return lengthOk(iso, cleaned) ? { iso, nsn: cleaned } : null;
}
/**
 * Document id for `divertSources`. Country-scoped because an NSN alone is not
 * globally unique, and Firestore ids must avoid `/` — `PL-600200300`.
 */
export function divertSourceKey(parts) {
    return `${parts.iso}-${parts.nsn}`;
}
/** Convenience: raw value → routing key, or null. */
export function divertSourceKeyFrom(raw, defaultIso) {
    const parts = toNsn(raw, defaultIso);
    return parts ? divertSourceKey(parts) : null;
}
/**
 * The diverting line as declared/observed, back in E.164 for display and for
 * storing on the business doc. `{ iso: 'PL', nsn: '600200300' }` → `+48600200300`.
 */
export function toE164(parts) {
    const cc = DIALING_CODES[parts.iso];
    return cc ? `+${cc}${parts.nsn}` : null;
}
/**
 * Which country an E.164 number belongs to, or `null` when its prefix is not
 * one we know (doc 21 D3 — SMS routing).
 *
 * Longest-prefix-first, so `+1` never shadows a longer code. Returning `null`
 * rather than guessing is the same discipline the rest of this module follows,
 * and it routes the message to the global vendor — the safe direction.
 */
export function isoFromE164(e164) {
    if (!e164 || !e164.startsWith('+'))
        return null;
    const digits = e164.slice(1);
    for (const [iso, code] of CODES_BY_LENGTH) {
        if (digits.startsWith(code) && lengthOk(iso, digits.slice(code.length)))
            return iso;
    }
    return null;
}
//# sourceMappingURL=nsn.js.map